import type { CalendarEvent, Persisted, Rider } from '@tunasong/schemas'

/**
 * Creating a rider for a gig (docs/ux/2026-10-16-rider-collaboration-ux.md, "Creating a rider for a gig"): usually a
 * copy of the last one with three things changed. Pure: the dialog decides, these build the draft.
 */

export interface CreateRiderBand {
  id: string
  name?: string
}

export interface CreateRiderInput {
  band: CreateRiderBand
  event?: Persisted<CalendarEvent>
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The date the event is on where the lead is: an all-day start is stored as the picked day's local midnight */
const localDate = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const trimmed = (value?: string | null) => value?.trim() || undefined

/** The rider's event fields from a calendar event; parts the event lacks are left out */
export const eventFields = (
  event: Persisted<CalendarEvent>
): Pick<Rider, 'eventName' | 'venue' | 'eventDate' | 'eventId'> => {
  const eventName = trimmed(event.name)
  const venue = trimmed(event.location)
  const eventDate = event.start ? localDate(event.start) : undefined
  return {
    ...(eventName ? { eventName } : {}),
    ...(venue ? { venue } : {}),
    ...(eventDate ? { eventDate } : {}),
    eventId: event.id,
  }
}

/** "{Band} rider · {event name or date}", or "{Band} rider" without an event */
const riderName = ({ band, event }: CreateRiderInput) => {
  const base = `${trimmed(band.name) ?? 'Band'} rider`
  if (!event) {
    return base
  }
  const fields = eventFields(event)
  const label = fields.eventName ?? fields.eventDate
  return label ? `${base} · ${label}` : base
}

/**
 * A new rider from the band's latest: its members (with their provenance, so edited rows stay edited), shared needs,
 * hospitality, advance contact and channel order. The old rider's event and identity never carry over.
 */
export const riderFromLatest = (latest: Rider, input: CreateRiderInput): Rider => ({
  type: 'rider',
  name: riderName(input),
  parentId: input.band.id,
  members: latest.members ?? [],
  ...(latest.shared ? { shared: latest.shared } : {}),
  ...(latest.hospitality ? { hospitality: latest.hospitality } : {}),
  ...(latest.advanceContact ? { advanceContact: latest.advanceContact } : {}),
  ...(latest.inputOrder ? { inputOrder: latest.inputOrder } : {}),
  ...(latest.generatedFromProfiles !== undefined ? { generatedFromProfiles: latest.generatedFromProfiles } : {}),
  ...(input.event ? eventFields(input.event) : {}),
})

/** A rider generated from members' setups, named and dated for the event when there is one */
export const riderFromEvent = (generated: Partial<Rider>, input: CreateRiderInput): Rider => ({
  ...generated,
  type: 'rider',
  members: generated.members ?? [],
  parentId: input.band.id,
  name: riderName(input),
  ...(input.event ? eventFields(input.event) : {}),
})

const isLive = (rider: Persisted<Rider>) => !(rider as { trash?: boolean }).trash

/** The band's most recently updated rider, the one "Create rider" starts from */
export const latestRider = (riders: Persisted<Rider>[]) =>
  riders
    .filter(isLive)
    .reduce<Persisted<Rider> | undefined>(
      (latest, rider) => (!latest || rider.updatedAt > latest.updatedAt ? rider : latest),
      undefined
    )

/** The rider already made for this calendar event, if any */
export const riderForEvent = (riders: Persisted<Rider>[], eventId: string | undefined) =>
  eventId ? riders.filter(isLive).find(rider => rider.eventId === eventId) : undefined
